// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName, type MedplumClient } from '@medplum/core';
import type {
  Appointment,
  Condition,
  Coverage,
  DocumentReference,
  Encounter,
  Extension,
  MedicationRequest,
  MedicationStatement,
  Observation,
  Organization,
  Patient,
  Practitioner,
  Provenance,
  ServiceRequest,
} from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { NOTE_CODES, type NoteCode } from '../components/encounter/encounter-chart-types';
import { HEIGHT_LOINC_CODE, LOINC_SYSTEM, WEIGHT_LOINC_CODE } from '../constants/patient-profile';
import { fetchPrimaryCoverage, resolvePayorOrganization } from './patient-coverage';
import { fetchPatientEncounterNotes, slugifyPatientName } from './patient-export';
import { fetchReferringProvider, getPractitionerNpi } from './patient-referring-provider';
import { formatCmToImperial, kgToLbs } from './patient-vitals';
import { getIdentifier, NPI_SYSTEM } from './practitioner';

const BRAND = '#640040';
const LIGHT_BRAND = '#f7ecf3';
const BORDER = '#d7c6d0';
const POLARIS_LOGO_URL = '/Logo-Black.png';
const NOTE_OBSERVATION_SYSTEM = 'https://polarissleep.com/fhir/observation-type';
const FHIR_DOCUMENT_COMPLETION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion';
const SIGNED_NOTE_SNAPSHOT_EXTENSION = 'https://polarissleep.com/fhir/StructureDefinition/signed-note-snapshot';
const ENCOUNTER_EXPORT_NOTE_CODES = [...NOTE_CODES, 'hpi', 'vital-signs', 'review-symptoms', 'exam'] as const;
type EncounterExportNoteCode = (typeof ENCOUNTER_EXPORT_NOTE_CODES)[number];

interface PdfTableCell {
  text?: string;
  style?: string | string[];
  alignment?: 'left' | 'center' | 'right';
  colSpan?: number;
  fillColor?: string;
  border?: [boolean, boolean, boolean, boolean];
  margin?: [number, number, number, number];
  [key: string]: unknown;
}

interface PdfSectionBlock {
  text?: string;
  style?: string | string[];
  margin?: [number, number, number, number];
  color?: string;
  alignment?: 'left' | 'center' | 'right';
  columns?: Record<string, unknown>[];
  stack?: Record<string, unknown>[];
  table?: {
    headerRows?: number;
    keepWithHeaderRows?: number;
    widths?: Array<string | number>;
    body: PdfTableCell[][];
  };
  layout?: string | Record<string, unknown>;
  canvas?: Record<string, unknown>[];
  pageBreak?: 'before' | 'after';
  [key: string]: unknown;
}

export interface EncounterNotePdfRecord {
  encounterId: string;
  title: string;
  status: string;
  dateOfService: string;
  encounterTime: string;
  patientName: string;
  patientDateOfBirth: string;
  patientSex: string;
  patientPhone: string;
  payer: string;
  memberId: string;
  renderingProvider: string;
  providerNpi: string;
  providerSpecialty: string;
  placeOfService: string;
  facility: string;
  referral: string;
  visitReason: string;
  hpi: string;
  vitalSigns: string;
  exam: string;
  assessment: string;
  treatmentPlan: string;
  noteForPatient: string;
  noteForReferringProvider: string;
  medications: Array<{
    medication: string;
    dosage: string;
    frequency: string;
    dateStarted: string;
    dateStopped: string;
  }>;
  conditions: Array<{
    condition: string;
    code: string;
    onsetDate: string;
    associatedMedication: string;
  }>;
  diagnoses: Array<{
    diagnosis: string;
    code: string;
    label: string;
  }>;
  orders: Array<{
    category: string;
    item: string;
    source: string;
    status: string;
  }>;
  signatureLabel: string;
  signatureTimestamp: string;
  signatureProvider: string;
  addendums: Array<{
    author: string;
    timestamp: string;
    text: string;
  }>;
}

function asDocumented(value: string | undefined | null): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : 'Not documented';
}

function formatDate(value: string | undefined | null): string {
  if (!value) {
    return 'Not documented';
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMMM D, YYYY') : 'Not documented';
}

function formatShortDate(value: string | undefined | null): string {
  if (!value) {
    return 'Not documented';
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : 'Not documented';
}

function formatDateTime(value: string | undefined | null): string {
  if (!value) {
    return 'Not documented';
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : 'Not documented';
}

function formatTimeRange(start: string | undefined, end: string | undefined): string {
  const parsedStart = start ? dayjs(start) : undefined;
  const parsedEnd = end ? dayjs(end) : undefined;
  if (!parsedStart?.isValid() || !parsedEnd?.isValid()) {
    return 'Not documented';
  }

  const timeZone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(parsedStart.toDate())
    .find((part) => part.type === 'timeZoneName')?.value;

  return `${parsedStart.format('h:mm A')} - ${parsedEnd.format('h:mm A')}${timeZone ? ` ${timeZone}` : ''}`;
}

function formatPhone(patient: Patient): string {
  return asDocumented(patient.telecom?.find((entry) => entry.system === 'phone')?.value);
}

function formatSex(patient: Patient): string {
  if (!patient.gender) {
    return 'Not documented';
  }

  return patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1);
}

function getEncounterStatus(encounter: Encounter, provenances: Provenance[]): string {
  if (provenances.length > 0) {
    const completionCoding = provenances
      .flatMap((provenance) => provenance.reason ?? [])
      .flatMap((reason) => reason.coding ?? [])
      .find((coding) => coding.system === FHIR_DOCUMENT_COMPLETION_SYSTEM);

    if (completionCoding?.code === 'LA') {
      return 'SIGNED & LOCKED';
    }

    return 'SIGNED';
  }

  if (encounter.status === 'finished') {
    return 'COMPLETED';
  }

  return 'DRAFT';
}

function getPatientDisplayName(patient: Patient): string {
  return patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Not documented';
}

function getEncounterTitle(encounter: Encounter, appointment: Appointment | undefined): string {
  return (
    appointment?.appointmentType?.coding?.[0]?.display ??
    encounter.type?.[0]?.text ??
    encounter.type?.[0]?.coding?.[0]?.display ??
    encounter.reasonCode?.[0]?.text ??
    'Encounter Note'
  );
}

function getProviderName(practitioner: Practitioner | undefined): string {
  return practitioner?.name?.[0] ? formatHumanName(practitioner.name[0]) : 'Not documented';
}

function getPlaceOfService(encounter: Encounter, appointment: Appointment | undefined): string {
  const locationDisplay = encounter.location?.[0]?.location?.display;
  if (locationDisplay) {
    return locationDisplay;
  }

  const classDisplay = encounter.class?.display ?? encounter.class?.code;
  const serviceType = appointment?.serviceType?.[0]?.text ?? appointment?.serviceType?.[0]?.coding?.[0]?.display;
  return asDocumented(serviceType ?? classDisplay);
}

function getFacility(encounter: Encounter): string {
  return asDocumented(encounter.serviceProvider?.display);
}

function getReferralLabel(
  referringProvider: Practitioner | undefined,
  referringOrganization: Organization | undefined,
  isSelfReferral: boolean
): string {
  if (isSelfReferral) {
    return 'self referral';
  }

  const providerName = referringProvider?.name?.[0] ? formatHumanName(referringProvider.name[0]) : '';
  const organizationName = referringOrganization?.name ?? '';
  if ([providerName, organizationName].some((name) => name.trim().toLowerCase() === 'self')) {
    return 'self referral';
  }

  return asDocumented([providerName, organizationName].filter(Boolean).join(' / '));
}

function getDiagnosisConditionIds(encounter: Encounter): string[] {
  return (
    encounter.diagnosis
      ?.map((diagnosis) => diagnosis.condition?.reference?.split('/')[1])
      .filter((id): id is string => Boolean(id)) ?? []
  );
}

function getNoteTextMap(observations: Observation[]): Record<EncounterExportNoteCode, string> {
  const loadedNotes: Record<EncounterExportNoteCode, string> = {
    assessment: '',
    exam: '',
    hpi: '',
    'treatment-plan': '',
    'vital-signs': '',
    'review-symptoms': '',
    'patient-note': '',
    'referring-note': '',
  };

  for (const observation of observations) {
    const code = observation.code?.coding?.find((coding) => coding.system === NOTE_OBSERVATION_SYSTEM)?.code as
      | EncounterExportNoteCode
      | NoteCode
      | undefined;

    if (code) {
      loadedNotes[code] = observation.valueString ?? '';
    }
  }

  return loadedNotes;
}

function formatVitalSigns(noteText: string, heightCm: number | undefined, weightKg: number | undefined): string {
  if (noteText.trim()) {
    return noteText;
  }

  const rows: string[] = [];

  if (heightCm !== undefined && Number.isFinite(heightCm)) {
    rows.push(`Height: ${formatCmToImperial(heightCm)} (${heightCm} cm)`);
  }

  if (weightKg !== undefined && Number.isFinite(weightKg)) {
    rows.push(`Weight: ${kgToLbs(weightKg)} lbs (${weightKg} kg)`);
  }

  return rows.length > 0 ? rows.join('\n') : 'Not documented';
}

function getMedicationLabel(statement: MedicationStatement): string {
  return (
    statement.medicationCodeableConcept?.text ??
    statement.medicationCodeableConcept?.coding?.[0]?.display ??
    statement.medicationCodeableConcept?.coding?.[0]?.code ??
    ''
  );
}

function getMedicationMap(statements: MedicationStatement[]): Map<string, string> {
  return new Map(
    statements
      .filter((statement) => statement.id)
      .map((statement) => [statement.id as string, getMedicationLabel(statement)])
  );
}

function formatMedicationRows(statements: MedicationStatement[]): EncounterNotePdfRecord['medications'] {
  if (statements.length === 0) {
    return [
      {
        medication: 'Not documented',
        dosage: 'Not documented',
        frequency: 'Not documented',
        dateStarted: 'Not documented',
        dateStopped: 'Not documented',
      },
    ];
  }

  return statements.map((statement) => {
    const dosage = statement.dosage?.[0];
    return {
      medication: asDocumented(getMedicationLabel(statement)),
      dosage: asDocumented(dosage?.text),
      frequency: asDocumented(dosage?.timing?.code?.text ?? dosage?.timing?.code?.coding?.[0]?.code),
      dateStarted: formatShortDate(statement.effectiveDateTime ?? statement.effectivePeriod?.start),
      dateStopped: statement.effectivePeriod?.end ? formatShortDate(statement.effectivePeriod.end) : 'Ongoing',
    };
  });
}

function formatConditionRows(
  conditions: Condition[],
  diagnosisConditionIds: Set<string>,
  medicationMap: Map<string, string>
): EncounterNotePdfRecord['conditions'] {
  const filteredConditions = conditions.filter((condition) => {
    if (!condition.id || diagnosisConditionIds.has(condition.id)) {
      return false;
    }

    return true;
  });

  if (filteredConditions.length === 0) {
    return [
      {
        condition: 'Not documented',
        code: 'Not documented',
        onsetDate: 'Not documented',
        associatedMedication: 'Not documented',
      },
    ];
  }

  return filteredConditions.map((condition) => ({
    condition: asDocumented(condition.code?.text ?? condition.code?.coding?.[0]?.display),
    code: asDocumented(condition.code?.coding?.[0]?.code),
    onsetDate: formatShortDate(condition.onsetDateTime),
    associatedMedication: asDocumented(
      condition.evidence
        ?.flatMap((evidence) => evidence.detail ?? [])
        .map((detail) => detail.reference?.split('/')[1])
        .filter((id): id is string => Boolean(id))
        .map((id) => medicationMap.get(id))
        .filter(Boolean)
        .join(', ')
    ),
  }));
}

function formatDiagnosisRows(
  encounter: Encounter,
  diagnosisConditions: Array<Condition | undefined>
): EncounterNotePdfRecord['diagnoses'] {
  const rows = (encounter.diagnosis ?? []).map((diagnosis, index) => {
    const condition = diagnosisConditions[index];
    const isPrimary = diagnosis.use?.coding?.[0]?.code === 'principal' || index === 0;

    return {
      diagnosis: asDocumented(condition?.code?.text ?? condition?.code?.coding?.[0]?.display),
      code: asDocumented(condition?.code?.coding?.[0]?.code),
      label: isPrimary ? 'Primary' : 'Additional',
    };
  });

  return rows.length > 0
    ? rows
    : [
        {
          diagnosis: 'Not documented',
          code: 'Not documented',
          label: 'Primary',
        },
      ];
}

function getMedicationRequestLabel(medicationRequest: MedicationRequest): string {
  return (
    medicationRequest.medicationCodeableConcept?.text ??
    medicationRequest.medicationCodeableConcept?.coding?.[0]?.display ??
    medicationRequest.medicationCodeableConcept?.coding?.[0]?.code ??
    ''
  );
}

function formatOrderRows(
  serviceRequests: ServiceRequest[],
  medicationRequests: MedicationRequest[]
): EncounterNotePdfRecord['orders'] {
  const rows = [
    ...serviceRequests.map((serviceRequest) => ({
      category: asDocumented(
        serviceRequest.category?.[0]?.text ?? serviceRequest.category?.[0]?.coding?.[0]?.display ?? 'Service Request'
      ),
      item: asDocumented(serviceRequest.code?.text ?? serviceRequest.code?.coding?.[0]?.display),
      source: asDocumented(
        serviceRequest.performer?.[0]?.display ??
          serviceRequest.requester?.display ??
          serviceRequest.bodySite?.[0]?.text
      ),
      status: asDocumented(serviceRequest.status),
    })),
    ...medicationRequests.map((medicationRequest) => ({
      category: 'Prescription',
      item: asDocumented(getMedicationRequestLabel(medicationRequest)),
      source: asDocumented(medicationRequest.requester?.display),
      status: asDocumented(medicationRequest.status),
    })),
  ];

  return rows.length > 0
    ? rows
    : [
        {
          category: 'Not documented',
          item: 'Not documented',
          source: 'Not documented',
          status: 'Not documented',
        },
      ];
}

function formatAddendums(documentReferences: DocumentReference[]): EncounterNotePdfRecord['addendums'] {
  if (documentReferences.length === 0) {
    return [];
  }

  return documentReferences.map((documentReference) => ({
    author: asDocumented(documentReference.author?.[0]?.display),
    timestamp: formatDateTime(documentReference.date),
    text: asDocumented(
      documentReference.content?.[0]?.attachment?.data ? atob(documentReference.content[0].attachment.data) : ''
    ),
  }));
}

async function readEncounterNoteRecord(
  medplum: MedplumClient,
  patient: Patient,
  encounterId: string,
  coverage: Coverage | undefined,
  payor: Organization | undefined,
  referringProvider: Practitioner | undefined,
  referringOrganization: Organization | undefined,
  isSelfReferral: boolean
): Promise<EncounterNotePdfRecord> {
  const encounter = await medplum.readResource('Encounter', encounterId);

  const diagnosisConditionIds = getDiagnosisConditionIds(encounter);
  const [
    appointment,
    practitioner,
    noteObservations,
    medicationStatements,
    conditions,
    serviceRequests,
    medicationRequests,
    provenances,
    addendumDocs,
    diagnosisConditions,
    latestHeight,
    latestWeight,
  ] = await Promise.all([
    encounter.appointment?.at(-1)?.reference?.startsWith('Appointment/')
      ? medplum.readReference<Appointment>(encounter.appointment.at(-1) as { reference: string }).catch(() => undefined)
      : Promise.resolve(undefined),
    encounter.participant?.[0]?.individual?.reference?.startsWith('Practitioner/')
      ? medplum
          .readReference<Practitioner>(encounter.participant[0].individual as { reference: string })
          .catch(() => undefined)
      : Promise.resolve(undefined),
    medplum.searchResources(
      'Observation',
      `encounter=Encounter/${encounter.id}&code=${ENCOUNTER_EXPORT_NOTE_CODES.map((code) => `${NOTE_OBSERVATION_SYSTEM}|${code}`).join(',')}`,
      { cache: 'no-cache' }
    ),
    // Medical Info stores medications and conditions at patient scope. Include those
    // resources in every encounter export; encounter-linked diagnosis conditions are
    // removed from the existing-conditions table below to avoid duplicate diagnoses.
    medplum.searchResources('MedicationStatement', `patient=Patient/${patient.id}`),
    medplum.searchResources('Condition', `patient=Patient/${patient.id}`),
    medplum.searchResources('ServiceRequest', `encounter=Encounter/${encounter.id}`),
    medplum.searchResources('MedicationRequest', `encounter=Encounter/${encounter.id}`),
    medplum.searchResources('Provenance', `target=Encounter/${encounter.id}`),
    medplum.searchResources('DocumentReference', {
      encounter: `Encounter/${encounter.id}`,
      type: '55107-7',
      _sort: '-date',
    }),
    Promise.all(
      diagnosisConditionIds.map((conditionId) => medplum.readResource('Condition', conditionId).catch(() => undefined))
    ),
    patient.id
      ? medplum.searchOne('Observation', {
          subject: `Patient/${patient.id}`,
          code: `${LOINC_SYSTEM}|${HEIGHT_LOINC_CODE}`,
          _sort: '-date',
          _count: '1',
        })
      : Promise.resolve(undefined),
    patient.id
      ? medplum.searchOne('Observation', {
          subject: `Patient/${patient.id}`,
          code: `${LOINC_SYSTEM}|${WEIGHT_LOINC_CODE}`,
          _sort: '-date',
          _count: '1',
        })
      : Promise.resolve(undefined),
  ]);

  const noteTextMap = getNoteTextMap(noteObservations);
  const signedSnapshotText = encounter.extension?.find(
    (extension: Extension) => extension.url === SIGNED_NOTE_SNAPSHOT_EXTENSION
  )?.valueString;
  if (signedSnapshotText && provenances.length > 0) {
    try {
      const snapshot = JSON.parse(signedSnapshotText) as { vitalSigns?: string };
      if (snapshot.vitalSigns) {
        noteTextMap['vital-signs'] = snapshot.vitalSigns;
      }
    } catch {
      // Ignore malformed legacy snapshots and use persisted encounter observations.
    }
  }
  const medicationMap = getMedicationMap(medicationStatements);

  return {
    encounterId,
    title: getEncounterTitle(encounter, appointment),
    status: getEncounterStatus(encounter, provenances),
    dateOfService: formatDate(encounter.period?.start ?? appointment?.start ?? encounter.meta?.lastUpdated),
    encounterTime: formatTimeRange(
      encounter.period?.start ?? appointment?.start,
      encounter.period?.end ?? appointment?.end
    ),
    patientName: getPatientDisplayName(patient),
    patientDateOfBirth: formatDate(patient.birthDate),
    patientSex: formatSex(patient),
    patientPhone: formatPhone(patient),
    payer: asDocumented(payor?.name),
    memberId: asDocumented(coverage?.subscriberId),
    renderingProvider: getProviderName(practitioner),
    providerNpi: asDocumented(getIdentifier(practitioner ?? ({} as Practitioner), NPI_SYSTEM)),
    providerSpecialty: 'Sleep Medicine',
    placeOfService: getPlaceOfService(encounter, appointment),
    facility: getFacility(encounter),
    referral: getReferralLabel(referringProvider, referringOrganization, isSelfReferral),
    visitReason: asDocumented(encounter.reasonCode?.[0]?.text),
    hpi: asDocumented(noteTextMap.hpi),
    vitalSigns: formatVitalSigns(
      noteTextMap['vital-signs'],
      latestHeight?.valueQuantity?.value,
      latestWeight?.valueQuantity?.value
    ),
    exam: asDocumented(noteTextMap.exam),
    assessment: asDocumented(noteTextMap.assessment),
    treatmentPlan: asDocumented(noteTextMap['treatment-plan']),
    noteForPatient: asDocumented(noteTextMap['patient-note']),
    noteForReferringProvider: asDocumented(noteTextMap['referring-note']),
    medications: formatMedicationRows(medicationStatements),
    conditions: formatConditionRows(conditions, new Set(diagnosisConditionIds), medicationMap),
    diagnoses: formatDiagnosisRows(encounter, diagnosisConditions),
    orders: formatOrderRows(serviceRequests, medicationRequests),
    signatureLabel: provenances.length > 0 ? 'Signed electronically' : 'Not documented',
    signatureTimestamp: provenances.length > 0 ? formatDateTime(provenances.at(-1)?.recorded) : 'Not documented',
    signatureProvider:
      provenances.length > 0
        ? asDocumented(provenances.at(-1)?.agent?.[0]?.who?.display ?? getProviderName(practitioner))
        : 'Not documented',
    addendums: formatAddendums(addendumDocs),
  };
}

export async function fetchPatientEncounterNotePdfData(
  medplum: MedplumClient,
  patient: Patient
): Promise<EncounterNotePdfRecord[]> {
  if (!patient.id) {
    return [];
  }

  const notes = await fetchPatientEncounterNotes(medplum, patient.id);
  if (notes.length === 0) {
    return [];
  }

  const [coverage, referring, selfReferralComposition] = await Promise.all([
    fetchPrimaryCoverage(medplum, patient.id),
    fetchReferringProvider(medplum, patient),
    medplum
      .searchOne('Composition', {
        subject: `Patient/${patient.id}`,
        type: 'http://polarissleep.com/composition-types|self-referral-intake',
        _sort: '-date',
      })
      .catch(() => undefined),
  ]);
  const payor = coverage ? await resolvePayorOrganization(medplum, coverage) : undefined;

  return Promise.all(
    notes.map((note) =>
      readEncounterNoteRecord(
        medplum,
        patient,
        note.encounterId,
        coverage,
        payor,
        referring.practitioner,
        referring.organization,
        Boolean(selfReferralComposition)
      )
    )
  );
}

function buildKeyValueGrid(title: string, entries: Array<{ label: string; value: string }>): PdfSectionBlock {
  return {
    stack: [
      { text: title, style: 'cardTitle', margin: [0, 0, 0, 10] },
      ...entries.map((entry) => ({
        columns: [
          { text: entry.label, style: 'labelText', width: 120 },
          { text: asDocumented(entry.value), style: 'valueText', width: '*' },
        ],
        margin: [0, 0, 0, 6],
      })),
    ],
    margin: [0, 0, 0, 12],
  };
}

function buildSimpleTable(headers: string[], rows: string[][], widths: Array<string | number>): PdfSectionBlock {
  const body: PdfTableCell[][] = [
    headers.map((header) => ({
      text: header,
      style: 'tableHeader',
      fillColor: LIGHT_BRAND,
    })),
    ...rows.map((row) => row.map((cell) => ({ text: asDocumented(cell), style: 'tableCell' }))),
  ];

  return {
    table: {
      headerRows: 1,
      keepWithHeaderRows: 1,
      widths,
      body,
    },
    layout: 'lightHorizontalLines',
    margin: [0, 8, 0, 18],
  };
}

function buildSectionHeading(title: string): PdfSectionBlock[] {
  return [
    {
      columns: [{ text: title, style: 'sectionTitle' }],
      margin: [0, 12, 0, 4],
    },
    {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: BORDER,
        },
      ],
      margin: [0, 0, 0, 10],
    },
  ];
}

function buildLogoBlock(logoDataUrl: string | undefined): Record<string, unknown> {
  if (logoDataUrl) {
    return {
      image: logoDataUrl,
      width: 170,
      fit: [170, 52],
    };
  }

  return {
    stack: [{ text: 'POLARIS', style: 'brandTitle' }],
  };
}

function buildNotePage(
  note: EncounterNotePdfRecord,
  isFirst: boolean,
  logoDataUrl: string | undefined
): PdfSectionBlock[] {
  const page: PdfSectionBlock[] = [];

  page.push({
    columns: [
      buildLogoBlock(logoDataUrl),
      {
        width: 210,
        stack: [
          { text: 'ENCOUNTER NOTE', style: 'pageTitle', alignment: 'right' },
          { text: 'Clinical documentation for care coordination', style: 'subtitle', alignment: 'right' },
          {
            text: note.status,
            style: 'statusPill',
            alignment: 'center',
            margin: [70, 10, 0, 0],
          },
        ],
      },
    ],
    margin: [0, isFirst ? 0 : 8, 0, 14],
    ...(isFirst ? {} : { pageBreak: 'before' }),
  });

  page.push(
    buildSimpleTable(
      ['DATE OF SERVICE', 'VISIT TYPE', 'ENCOUNTER TIME'],
      [[note.dateOfService, note.title, note.encounterTime]],
      ['33%', '33%', '34%']
    )
  );

  page.push({
    columns: [
      buildKeyValueGrid('PATIENT', [
        { label: 'Name', value: note.patientName },
        { label: 'Date of Birth', value: note.patientDateOfBirth },
        { label: 'Sex', value: note.patientSex },
        { label: 'Phone', value: note.patientPhone },
        { label: 'Payer / Member ID', value: `${note.payer} / ${note.memberId}` },
      ]),
      buildKeyValueGrid('ENCOUNTER & PROVIDER', [
        { label: 'Rendering Provider', value: note.renderingProvider },
        { label: 'NPI', value: note.providerNpi },
        { label: 'Specialty', value: note.providerSpecialty },
        { label: 'Place of Service', value: note.placeOfService },
        { label: 'Referral', value: note.referral },
      ]),
    ],
    columnGap: 16,
    margin: [0, 0, 0, 8],
  });

  page.push(...buildSectionHeading('VISIT REASON'));
  page.push({ text: note.visitReason, style: 'bodyText', margin: [0, 0, 0, 10] });

  page.push(...buildSectionHeading('HPI'));
  page.push({ text: note.hpi, style: 'bodyText', margin: [0, 0, 0, 10] });

  page.push(...buildSectionHeading('VITAL SIGNS'));
  page.push({ text: note.vitalSigns, style: 'bodyText', margin: [0, 0, 0, 10] });

  page.push(...buildSectionHeading('EXAM'));
  page.push({ text: note.exam, style: 'bodyText', margin: [0, 0, 0, 10] });

  page.push(...buildSectionHeading('ASSESSMENT'));
  page.push({ text: note.assessment, style: 'bodyText', margin: [0, 0, 0, 10] });

  page.push(...buildSectionHeading('CURRENT MEDICATIONS'));
  page.push(
    buildSimpleTable(
      ['MEDICATION', 'DOSAGE', 'FREQUENCY', 'DATE STARTED', 'DATE STOPPED'],
      note.medications.map((row) => [row.medication, row.dosage, row.frequency, row.dateStarted, row.dateStopped]),
      ['28%', '16%', '16%', '20%', '20%']
    )
  );

  page.push(...buildSectionHeading('EXISTING CONDITIONS'));
  page.push(
    buildSimpleTable(
      ['CONDITION', 'CODE', 'ONSET DATE', 'ASSOCIATED MEDICATION'],
      note.conditions.map((row) => [row.condition, row.code, row.onsetDate, row.associatedMedication]),
      ['34%', '14%', '18%', '34%']
    )
  );

  page.push(...buildSectionHeading('DIAGNOSES'));
  page.push(
    buildSimpleTable(
      ['LABEL', 'DIAGNOSIS', 'ICD-10'],
      note.diagnoses.map((row) => [row.label, row.diagnosis, row.code]),
      ['18%', '58%', '24%']
    )
  );

  page.push(...buildSectionHeading('ORDERS PLACED'));
  page.push(
    buildSimpleTable(
      ['CATEGORY', 'ITEM', 'SOURCE', 'STATUS'],
      note.orders.map((row) => [row.category, row.item, row.source, row.status]),
      ['20%', '34%', '28%', '18%']
    )
  );

  page.push(...buildSectionHeading('TREATMENT PLAN'));
  page.push({ text: note.treatmentPlan, style: 'bodyText', margin: [0, 0, 0, 10] });

  page.push(...buildSectionHeading('NOTE FOR PATIENT'));
  page.push({ text: note.noteForPatient, style: 'bodyText', margin: [0, 0, 0, 10] });

  page.push(...buildSectionHeading('NOTE FOR REFERRING PROVIDER'));
  page.push({ text: note.noteForReferringProvider, style: 'bodyText', margin: [0, 0, 0, 10] });

  if (note.addendums.length > 0) {
    page.push(...buildSectionHeading('ADDENDA'));
    for (const addendum of note.addendums) {
      page.push({
        stack: [
          { text: `${addendum.author} • ${addendum.timestamp}`, style: 'labelText' },
          { text: addendum.text, style: 'bodyText' },
        ],
        margin: [0, 0, 0, 10],
      });
    }
  }

  page.push(...buildSectionHeading('ATTESTATION & ELECTRONIC SIGNATURE'));
  page.push({
    table: {
      widths: ['60%', '40%'],
      body: [
        [
          {
            text: 'I attest that I personally performed or supervised the services documented above, and that this record is accurate and complete to the best of my knowledge.',
            style: 'attestationText',
            border: [true, true, false, true],
          },
          {
            stack: [
              {
                canvas: [
                  {
                    type: 'line',
                    x1: 110,
                    y1: 0,
                    x2: 220,
                    y2: 0,
                    lineWidth: 1,
                    lineColor: '#9d8b96',
                  },
                ],
                margin: [0, 0, 0, 6],
              },
              { text: note.signatureProvider, style: 'signatureProvider', alignment: 'right' },
              { text: note.signatureLabel, style: 'signatureMeta', alignment: 'right', margin: [0, 6, 0, 0] },
              { text: note.signatureTimestamp, style: 'signatureMeta', alignment: 'right', margin: [0, 2, 0, 0] },
            ],
            border: [false, true, true, true],
          },
        ],
      ],
    },
    layout: 'noBorders',
    margin: [0, 6, 0, 0],
  });

  return page;
}

export function buildEncounterNotesPdfDefinition(
  patient: Patient,
  notes: EncounterNotePdfRecord[],
  logoDataUrl?: string
): Record<string, unknown> {
  return {
    pageSize: 'LETTER',
    pageMargins: [40, 34, 40, 40],
    defaultStyle: {
      font: 'Roboto',
      fontSize: 10,
      color: '#231f20',
    },
    styles: {
      brandTitle: {
        fontSize: 32,
        bold: true,
        color: '#111111',
      },
      pageTitle: {
        fontSize: 22,
        bold: true,
        color: '#231f20',
      },
      subtitle: {
        fontSize: 10,
        color: '#6b6470',
      },
      statusPill: {
        bold: true,
        color: 'white',
        fillColor: BRAND,
        margin: [0, 0, 0, 0],
      },
      cardTitle: {
        fontSize: 11,
        bold: true,
        color: BRAND,
      },
      sectionTitle: {
        fontSize: 12,
        bold: true,
        color: BRAND,
      },
      labelText: {
        fontSize: 9,
        color: '#6b6470',
      },
      valueText: {
        fontSize: 10,
        bold: true,
      },
      bodyText: {
        fontSize: 10,
        lineHeight: 1.35,
      },
      tableHeader: {
        fontSize: 9,
        bold: true,
        color: BRAND,
        margin: [0, 4, 0, 4],
      },
      tableCell: {
        fontSize: 9,
        margin: [0, 3, 0, 3],
      },
      attestationText: {
        fontSize: 9,
        color: '#6b6470',
        margin: [0, 6, 8, 6],
      },
      signatureProvider: {
        fontSize: 16,
        bold: true,
        color: '#231f20',
        margin: [0, 0, 0, 4],
      },
      signatureMeta: {
        fontSize: 9,
        color: '#6b6470',
      },
    },
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        {
          text: `Generated by Polaris Sleep • ${formatDate(dayjs().toISOString())}`,
          margin: [40, 0, 0, 0],
          fontSize: 8,
          color: '#6b6470',
        },
        {
          text: `Patient: ${getPatientDisplayName(patient)} • ${currentPage}/${pageCount}`,
          alignment: 'right',
          margin: [0, 0, 40, 0],
          fontSize: 8,
          color: '#6b6470',
        },
      ],
    }),
    content: notes.flatMap((note, index) => buildNotePage(note, index === 0, logoDataUrl)),
  };
}

function escapeCsvCell(value: string): string {
  return `"${value.replaceAll('"', '""')}"`;
}

function formatCsvList(rows: string[], emptyValue = 'Not documented'): string {
  return rows.length > 0 ? rows.join(' | ') : emptyValue;
}

export function buildEncounterNotesDetailedCsv(notes: EncounterNotePdfRecord[]): string {
  const header = [
    'Encounter ID',
    'Visit Type',
    'Status',
    'Date of Service',
    'Encounter Time',
    'Patient Name',
    'Patient Date of Birth',
    'Patient Sex',
    'Patient Phone',
    'Payer',
    'Member ID',
    'Rendering Provider',
    'Provider NPI',
    'Provider Specialty',
    'Place of Service',
    'Facility',
    'Referral',
    'Visit Reason',
    'HPI',
    'Vital Signs',
    'Exam',
    'Assessment',
    'Treatment Plan',
    'Note for Patient',
    'Note for Referring Provider',
    'Medications',
    'Conditions',
    'Diagnoses',
    'Orders',
    'Signature Label',
    'Signature Timestamp',
    'Signature Provider',
    'Addendums',
  ];

  const rows = notes.map((note) => [
    note.encounterId,
    note.title,
    note.status,
    note.dateOfService,
    note.encounterTime,
    note.patientName,
    note.patientDateOfBirth,
    note.patientSex,
    note.patientPhone,
    note.payer,
    note.memberId,
    note.renderingProvider,
    note.providerNpi,
    note.providerSpecialty,
    note.placeOfService,
    note.facility,
    note.referral,
    note.visitReason,
    note.hpi,
    note.vitalSigns,
    note.exam,
    note.assessment,
    note.treatmentPlan,
    note.noteForPatient,
    note.noteForReferringProvider,
    formatCsvList(
      note.medications.map(
        (medication) =>
          `${medication.medication}; dosage=${medication.dosage}; frequency=${medication.frequency}; started=${medication.dateStarted}; stopped=${medication.dateStopped}`
      )
    ),
    formatCsvList(
      note.conditions.map(
        (condition) =>
          `${condition.condition}; code=${condition.code}; onset=${condition.onsetDate}; medication=${condition.associatedMedication}`
      )
    ),
    formatCsvList(
      note.diagnoses.map((diagnosis) => `${diagnosis.label}; ${diagnosis.diagnosis}; code=${diagnosis.code}`)
    ),
    formatCsvList(
      note.orders.map((order) => `${order.category}; ${order.item}; source=${order.source}; status=${order.status}`)
    ),
    note.signatureLabel,
    note.signatureTimestamp,
    note.signatureProvider,
    formatCsvList(note.addendums.map((addendum) => `${addendum.author}; ${addendum.timestamp}; ${addendum.text}`)),
  ]);

  return [header, ...rows].map((row) => row.map((value) => escapeCsvCell(value)).join(',')).join('\n');
}

export async function downloadEncounterNotesPdf(patient: Patient, notes: EncounterNotePdfRecord[]): Promise<void> {
  await downloadEncounterNotesPdfWithFileName(patient, notes, `${slugifyPatientName(patient)}-encounter-notes.pdf`);
}

export async function downloadEncounterNotesPdfWithFileName(
  patient: Patient,
  notes: EncounterNotePdfRecord[],
  fileName: string
): Promise<void> {
  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const pdfFontsModule = await import('pdfmake/build/vfs_fonts');

  const pdfMake = pdfMakeModule.default;
  const pdfFonts = pdfFontsModule.default;
  const logoDataUrl = await loadPolarisLogoDataUrl();
  pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? pdfMake.vfs;
  pdfMake.createPdf(buildEncounterNotesPdfDefinition(patient, notes, logoDataUrl)).download(fileName);
}

async function loadPolarisLogoDataUrl(): Promise<string | undefined> {
  try {
    const response = await fetch(POLARIS_LOGO_URL);
    if (!response.ok) {
      return undefined;
    }

    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}
